import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyGetAgenticRunEventsQuery } from "../../../app/api/console/agenticRunsApiSlice";
import { EventsTimelineCleanView, EventsTimelineRawView } from "./eventsTimelineView";
import ConversationView from "./ConversationView";

const VIEW_MODES = [
  ["conversation", "Conversation"],
  ["clean", "Clean"],
  ["raw", "Raw"],
];

const MAX_EVENT_PAGES = 200;
const EVENTS_PAGE_SIZE = 500;

function filterEventsForRun(events, focusRunId) {
  if (!focusRunId) return events;
  const runIds = new Set([focusRunId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const ev of events) {
      const rid = ev.run_id;
      const parent = ev.parent_run_id;
      if (parent && runIds.has(parent) && rid && !runIds.has(rid)) {
        runIds.add(rid);
        changed = true;
      }
    }
  }
  return events.filter((ev) => !ev.run_id || runIds.has(ev.run_id));
}

export default function EventsTimeline({
  env,
  runId,
  fanOuts,
  orchestratorRun,
  autoLoad = false,
  compact = false,
  focusRunId = null,
}) {
  const [enabled, setEnabled] = useState(autoLoad);
  const [viewMode, setViewMode] = useState("conversation");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [showSystemEvents, setShowSystemEvents] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [complete, setComplete] = useState(false);
  const [trigger] = useLazyGetAgenticRunEventsQuery();
  const loadingRef = useRef(false);

  const loadAll = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    setComplete(false);
    setAllEvents([]);

    const acc = [];
    let nextToken;
    try {
      for (let page = 0; page < MAX_EVENT_PAGES; page += 1) {
        const res = await trigger(
          { env, runId, nextToken, limit: EVENTS_PAGE_SIZE },
          false
        ).unwrap();
        if (res?.error) {
          setError(res.error);
          break;
        }
        acc.push(...(res?.events || []));
        setAllEvents([...acc]);
        nextToken = res?.next_token || null;
        if (!nextToken) {
          setComplete(true);
          break;
        }
      }
    } catch (e) {
      setError(e?.data?.detail || e?.error || "Failed to load events");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [trigger, env, runId]);

  useEffect(() => {
    if (enabled) loadAll();
  }, [enabled, loadAll]);

  const events = useMemo(() => {
    let list = allEvents;
    if (focusRunId) list = filterEventsForRun(list, focusRunId);
    if (errorsOnly) list = list.filter((e) => e.is_error);
    return list;
  }, [allEvents, errorsOnly, focusRunId]);

  if (!enabled) {
    return (
      <button
        type='button'
        onClick={() => setEnabled(true)}
        className='px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors'>
        Load events timeline (CloudWatch)
      </button>
    );
  }

  return (
    <div className='bg-white border border-gray-200 rounded-xl'>
      <div className='px-4 py-3 border-b border-gray-100 flex items-center justify-between'>
        <span className='text-sm font-medium text-gray-800'>
          Events{" "}
          {loading
            ? `(loading… ${events.length})`
            : `(${events.length}${complete ? "" : "+"})`}
        </span>
        <div className='flex items-center gap-3'>
          <div className='inline-flex rounded-lg border border-gray-200 bg-white p-0.5'>
            {VIEW_MODES.map(([m, label]) => (
              <button
                key={m}
                type='button'
                onClick={() => setViewMode(m)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  viewMode === m
                    ? "bg-indigo-600 text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}>
                {label}
              </button>
            ))}
          </div>
          <label className='flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none'>
            <input
              type='checkbox'
              checked={showSystemEvents}
              onChange={(e) => setShowSystemEvents(e.target.checked)}
              className='rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
            />
            system events
          </label>
          <label className='flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none'>
            <input
              type='checkbox'
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
              className='rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
            />
            errors / warnings only
          </label>
          <button
            type='button'
            onClick={loadAll}
            disabled={loading}
            className='text-xs font-medium text-indigo-700 hover:text-indigo-900 disabled:text-gray-400 disabled:cursor-not-allowed'>
            {loading ? "Loading…" : "Reload"}
          </button>
        </div>
      </div>
      <div
        className={`overflow-y-auto divide-y divide-gray-50 ${
          compact ? "max-h-[min(70vh,720px)]" : "max-h-[min(80vh,1100px)]"
        }`}>
        {error && <div className='px-4 py-3 text-xs text-red-600'>{error}</div>}
        {viewMode === "conversation" && (
          <ConversationView
            events={events}
            rootRunId={focusRunId || orchestratorRun?.run_id || runId}
            env={env}
            traceRunId={runId}
          />
        )}
        {viewMode === "clean" && (
          <EventsTimelineCleanView
            events={events}
            fanOuts={fanOuts}
            orchestratorRunId={focusRunId || orchestratorRun?.run_id || runId}
            orchestratorAgentId={orchestratorRun?.agent_id}
            orchestratorSkill={orchestratorRun?.skill_label}
            showSystemEvents={showSystemEvents}
          />
        )}
        {viewMode === "raw" && <EventsTimelineRawView events={events} />}
        {!loading && events.length === 0 && !error && (
          <div className='px-4 py-6 text-center text-xs text-gray-400'>
            No events found in the run's time window (CloudWatch retention may have expired for
            old runs).
          </div>
        )}
      </div>
    </div>
  );
}
